import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { firebaseConfig, db, appId } from './firebase/config'; 
import { doc, onSnapshot } from 'firebase/firestore'; 
import { useAuth, AuthProvider } from './hooks/useAuth'; 
import { useTheme, ThemeProvider } from './context/ThemeContext';

import FontLoader from './components/ui/FontLoader';
import { EditorProvider, useEditor } from './context/EditorContext';
import SidebarEditor from './components/editor/SidebarEditor';

import Navbar from './components/client/Navbar';
import Footer from './components/client/Footer';
import HomePage from './pages/HomePage';
import CookieBanner from './components/ui/CookieBanner';
import AccessibilityMenu from './components/ui/AccessibilityMenu';
import InstallPWA from './components/ui/InstallPWA'; 

// --- NOVO IMPORT ---
import MaintenanceGuard from './components/MaintenanceGuard';

// Páginas com Lazy Loading
const MenuPage = lazy(() => import('./pages/MenuPage'));
const WineMenuPage = lazy(() => import('./pages/WineMenuPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const GalleryPage = lazy(() => import('./pages/GalleryPage'));
const ReservationsPage = lazy(() => import('./pages/ReservationsPage'));
const EventsPage = lazy(() => import('./pages/EventsPage'));
const ClientAuthPage = lazy(() => import('./pages/auth/ClientAuthPage'));
const AdminLoginPage = lazy(() => import('./pages/AdminLoginPage'));
const AdminPanel = lazy(() => import('./pages/AdminPanel'));

const SimpleLoading = () => (
    <div className="h-screen w-screen flex items-center justify-center bg-white">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
);

const ClientLayout = ({ currentTheme }) => {
    const location = useLocation();
    const isHomePage = location.pathname === '/';

    return (
        <>
            <Navbar theme={currentTheme} />
            <main className="min-h-screen">
                <Suspense fallback={<SimpleLoading />}>
                    <Routes>
                        <Route path="/" element={<HomePage />} />
                        <Route path="/ementa" element={<MenuPage />} />
                        <Route path="/vinhos" element={<WineMenuPage />} />
                        <Route path="/sobre" element={<AboutPage />} />
                        <Route path="/galeria" element={<GalleryPage />} />
                        <Route path="/reservas" element={<ReservationsPage />} />
                        <Route path="/eventos" element={<EventsPage />} />
                        <Route path="/login" element={<ClientAuthPage />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </Suspense>
            </main>
            <Footer theme={currentTheme} />
            <CookieBanner />
            <AccessibilityMenu />
            <InstallPWA />
        </>
    );
};

function AppContent() {
    const { user, loading: authLoading } = useAuth();
    const { activeTheme } = useTheme();
    const { isEditMode, toggleEditMode } = useEditor();
    const location = useLocation();
    
    const isAdminPath = location.pathname.startsWith('/admin');
    const isLoginPage = location.pathname === '/admin/login';

    const [maintenanceActive, setMaintenanceActive] = useState(false);

    // Ouvinte em tempo real para o modo de manutenção
    useEffect(() => {
        const docRef = doc(db, 'artifacts/' + appId + '/public/data/configuracoes', 'sistema');
        const unsub = onSnapshot(docRef, (docSnap) => {
            if (docSnap.exists()) {
                setMaintenanceActive(docSnap.data().emManutencao || false);
            }
        });
        return () => unsub();
    }, []);

    if (authLoading) return <SimpleLoading />;

    const canEditDesign = user?.email?.endsWith('@vintagepraia.pt') && isAdminPath;

    return (
        <>
            {/* Componentes de Edição */}
            {canEditDesign && <SidebarEditor />}
            
            {canEditDesign && !isEditMode && (
                <button 
                    onClick={toggleEditMode} 
                    className="fixed bottom-6 right-6 z-[9999] bg-blue-600 hover:bg-blue-700 text-white p-4 rounded-full shadow-2xl transition-all transform hover:scale-110 flex items-center justify-center group" 
                    title="Editar Design"
                >
                    <i className="fas fa-paint-brush text-xl"></i>
                </button>
            )}

            <div className="relative min-h-screen transition-all duration-300">
                <Suspense fallback={<SimpleLoading />}>
                    <Routes>
                        {/* 1. Rotas Administrativas - Sempre acessíveis para gestão */}
                        <Route path="/admin/login" element={<AdminLoginPage />} />
                        <Route 
                            path="/admin/*" 
                            element={user?.email?.endsWith('@vintagepraia.pt') ? <AdminPanel /> : <Navigate to="/admin/login" replace />} 
                        />

                        {/* 2. Rotas Públicas - Agora protegidas pelo Guardião com Password */}
                        <Route 
                            path="/*" 
                            element={
                                <MaintenanceGuard maintenanceActive={maintenanceActive}>
                                    <ClientLayout currentTheme={activeTheme} />
                                </MaintenanceGuard>
                            } 
                        />
                    </Routes>
                </Suspense>
            </div>
        </>
    );
}

export default function App() {
    return (
        <AuthProvider>
            <ThemeProvider>
                <FontLoader />
                <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                    <EditorProvider>
                        <AppContent />
                    </EditorProvider>
                </BrowserRouter>
            </ThemeProvider>
        </AuthProvider>
    );
}